// Top bar (IA §2.1 "Top bar", §2.3 mobile top app bar): store switcher (tenancy always visible),
// search / command palette, Sell, notifications, help, assistant and the account menu.
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { t } from "@ady/i18n";
import { cx, Dialog, Icon, Logo } from "@ady/ui";
import { AssistantHello, HeaderMascot } from "../ai-mascot";
import { NotificationBell } from "../notifications/Bell";
import { TID } from "../testids";
import { AppLink, useGo } from "./AppLink";
import { AccountMenu } from "./AccountMenu";
import { HelpMenu } from "./HelpMenu";
import { RootDrawer } from "./RootDrawer";
import { rememberStore, useShell } from "./context";
import { can, hrefFor, iconFor, visibleNav } from "./nav";
import { WalletChip } from "../billing/WalletTopup";
import { sfApi, sfKeys, storeAddress } from "../modules/storefront/api";
import { VerifiedMark, verificationHref } from "./VerifiedMark";

export function StoreSwitcherDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { me, orgSlug, storeSlug } = useShell();
  const go = useGo();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("shell.storeSwitcher.title")}
      data-testid={TID.storeSwitcherDialog}
      dismissLabel={t("common.close")}
    >
      <ul className="flex flex-col gap-4">
        {me.memberships.map((m) => (
          <li key={m.org.id}>
            <p className="pb-1 text-sm font-medium text-fg-secondary" data-content="user">
              {m.org.name}
            </p>
            <ul className="flex flex-col gap-1">
              {m.stores.map((st) => {
                const current = m.org.slug === orgSlug && st.slug === storeSlug;
                return (
                  <li key={st.id}>
                    <button
                      type="button"
                      data-testid={TID.storeOption(m.org.slug, st.slug)}
                      aria-current={current ? "true" : undefined}
                      onClick={() => {
                        onClose();
                        rememberStore(m.org.slug, st.slug);
                        go(`/${m.org.slug}/${st.slug}/today`);
                      }}
                      className={cx(
                        "flex min-h-12 w-full items-center gap-3 rounded-sm border px-3 py-2 text-start",
                        current
                          ? "border-action-border bg-selected"
                          : "border-subtle hover:bg-hover",
                      )}
                    >
                      <Icon name="store" className="flex-none text-fg-secondary" />
                      <span className="flex flex-1 flex-col">
                        <span className="text-md font-medium" data-content="user">
                          {st.name}
                        </span>
                        <span className="text-xs text-fg-secondary">
                          {t("shell.storeSwitcher.roleAndPlan", {
                            role: m.role.name,
                            plan: m.plan.name,
                          })}
                        </span>
                      </span>
                      {current ? (
                        <span className="text-xs font-medium text-action-fg">
                          {t("shell.storeSwitcher.current")}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
    </Dialog>
  );
}

/** Account menu (security, sessions, theme, sign out). Also used by the no-business account layout. */
/** POS mode (owner, 2026-10-21): the Sell pages (sell screen, counter sales, registers) live in
 *  this header menu instead of the sidebar. Phones keep them in the bottom bar and the More menu. */
function PosMenu() {
  const { membership, orgSlug, storeSlug } = useShell();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const items = visibleNav(membership).find((g) => g.id === "sell")?.items ?? [];
  if (!items.length) return null;
  return (
    <div className="relative me-2 hidden md:block" ref={ref}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        data-testid={TID.sellButton}
        onClick={() => setOpen((o) => !o)}
        className="flex min-h-11 items-center gap-2 rounded-sm border border-link px-3 text-md font-medium whitespace-nowrap text-link hover:bg-selected lg:min-h-control lg:px-4 lg:text-sm"
      >
        <Icon name="sell" gradient />
        <span className="ady-text-brand">{t("shell.posMode")}</span>
        <Icon name="chevronDown" size={16} />
      </button>
      {open ? (
        <div
          role="menu"
          aria-label={t("shell.posMode")}
          className="absolute end-0 top-full z-(--ady-z-popover) mt-2 w-64 rounded-md border border-subtle bg-overlay p-1.5 shadow-2"
        >
          {items.map((item) => (
            <AppLink
              key={item.id}
              role="menuitem"
              href={hrefFor(item, orgSlug, storeSlug)}
              onClick={() => setOpen(false)}
              className="flex min-h-control items-center gap-3 rounded-xs px-3 text-sm text-fg no-underline hover:bg-hover"
            >
              <Icon name={iconFor(item)} className="text-fg-secondary" />
              {t(item.label)}
            </AppLink>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Beside the logo (owner, 2026-10-25): opens the store shoppers see, in a new tab. The dot is green
 * when the store is live; otherwise its status (coming soon, password…) shows on hover. */
function VisitShop() {
  const { membership, store } = useShell();
  const allowed = can(membership, ["store.read"]);
  const q = useQuery({
    queryKey: sfKeys.store(membership.org.id, store.id),
    queryFn: () => sfApi.store(membership.org.id, store.id),
    enabled: allowed,
    retry: false,
    staleTime: 60_000,
  });
  const s = q.data;
  if (!allowed || !s) return null;
  const status = t(`storefront.admin.settings.status.${s.status}`);
  const live = s.status === "live";
  return (
    <a
      href={storeAddress(s)}
      target="_blank"
      rel="noopener"
      aria-label={t("shell.visitShopLabel", { status })}
      title={live ? undefined : status}
      data-testid="visit-shop"
      className="hidden min-h-control flex-none items-center gap-1.5 rounded-full border border-card bg-surface px-3 text-sm font-medium text-link no-underline transition-colors duration-(--ady-dur-quick) hover:border-link hover:bg-selected md:inline-flex lg:ms-2"
    >
      <span
        className={cx("size-2 flex-none rounded-full", live ? "bg-success-fg" : "bg-warning-fg")}
        aria-hidden
      />
      {/* The words from 1280 px: at 1024 beside the full sidebar they squeezed search out of the bar. */}
      <span className="hidden xl:inline">{t("shell.visitShop")}</span>
      <Icon name="external" />
    </a>
  );
}

type Panel = "assistant" | null;

export { AccountMenu };

export function TopBar() {
  const { me, membership, store, orgSlug, storeSlug, openPalette, openStoreSwitcher } = useShell();
  const [panel, setPanel] = useState<Panel>(null);
  // Header tool buttons (owner, 2026-10-21): round, soft brand border, gradient icon, tint on hover —
  // the same family as the POS mode button and the account avatar.
  const iconBtn =
    "flex size-10 flex-none items-center justify-center rounded-full border border-card bg-surface transition-colors duration-(--ady-dur-quick) hover:border-link hover:bg-selected";
  const botBtn =
    "flex size-11 flex-none items-center justify-center rounded-full hover:bg-selected";
  return (
    <header
      data-testid={TID.topBar}
      className="sticky top-0 z-(--ady-z-sticky) flex h-16 flex-none items-center gap-2 border-b border-subtle bg-surface/95 px-2 backdrop-blur md:px-4 lg:h-18"
    >
      {/* Desktop (D-038): the logo leads the bar; the store switcher lives at the top of the sidebar. */}
      <span className="hidden lg:inline-flex">
        <Logo variant="lockup" height={30} />
      </span>
      <VisitShop />
      <button
        type="button"
        onClick={openStoreSwitcher}
        data-testid={TID.storeSwitcher}
        aria-label={t("shell.storeSwitcher.label", {
          store: store.name,
          org: membership.org.name,
        })}
        className="flex min-h-11 min-w-0 items-center gap-2 rounded-sm px-2 text-start hover:bg-hover lg:hidden"
      >
        <span className="flex min-w-0 flex-col">
          <span className="flex min-w-0 items-center gap-1">
            <span
              className="truncate text-md font-semibold leading-6"
              data-content="user"
              data-testid={TID.storeName}
            >
              {store.name}
            </span>
            <VerifiedMark
              verified={membership.org.verified}
              href={verificationHref(orgSlug, storeSlug)}
              inButton
            />
          </span>
          {/* The business is always visible (tenancy); role and plan from 640 px. */}
          <span className="flex min-w-0 gap-1 truncate text-xs text-fg-secondary">
            <span className="truncate" data-content="user">
              {membership.org.name}
            </span>
            <span className="hidden truncate sm:inline" aria-hidden="true">
              {"·"}
            </span>
            <span className="hidden truncate sm:inline">
              {t("shell.storeSwitcher.roleAndPlan", {
                role: membership.role.name,
                plan: membership.plan.name,
              })}
            </span>
          </span>
        </span>
        <Icon name="chevronDown" size={18} className="flex-none text-fg-secondary" />
      </button>

      <button
        type="button"
        onClick={openPalette}
        data-testid={TID.searchButton}
        aria-keyshortcuts="Control+K Meta+K /"
        // Tablet keeps the round search button too: the 288 px search box pushed the bell and the
        // account button off a 768 px screen (beside the sidebar rail).
        className="ms-auto flex size-11 flex-none items-center justify-center rounded-sm text-fg-secondary hover:bg-hover md:size-10 md:rounded-full md:border md:border-card md:bg-surface md:hover:border-link md:hover:bg-selected"
      >
        <Icon name="search" gradient />
        <span className="sr-only">{t("shell.search")}</span>
      </button>

      <div className="flex items-center gap-1 lg:gap-2">
        <WalletChip />
        <PosMenu />
        {/* NOTIFY-01 bell (P7-B): unread badge, drawer, live updates. */}
        <NotificationBell className={iconBtn} />
        <HelpMenu className={iconBtn} onAskRoot={() => setPanel("assistant")} />
        {/* From 1024 px; on a tablet root opens from the Help menu ("Ask root"). */}
        <span className="relative hidden flex-none lg:flex">
          <button
            type="button"
            className={cx(botBtn, "ady-bot-host")}
            aria-label={t("shell.openAssistant")}
            data-testid={TID.assistantButton}
            onClick={() => setPanel("assistant")}
          >
            <HeaderMascot size={36} />
          </button>
          <AssistantHello suppressed={panel === "assistant"} />
        </span>
        <AccountMenu me={me} />
      </div>

      <RootDrawer open={panel === "assistant"} onClose={() => setPanel(null)} />
    </header>
  );
}
